import {
  Component,
  OnInit,
  ChangeDetectorRef,
  DestroyRef,
  ElementRef,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  QaiButtonComponent,
  QaiIconComponent,
  QaiInnerWrapComponent,
  QaiFloatingSelectComponent,
  TableRow,
  QaiTabsComponent,
  QaiTabDirective,
  QaiFloatingSelectOption,
} from '@qai/ui';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthorReactiveFormService } from '../../services/author-reactive-form.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';
import { AssignmentsComponent, AssignmentStatusChange } from './assignments/assignments.component';
import { AssignmentSimulationsComponent } from './simulations/simulations.component';
import { ActionModalComponent } from '../../components/modals/simulation-assignment-action-modal/action-modal.component';
import { DeleteModalComponent } from '../../components/modals/delete-modal/delete-modal.component';
import { ISimulation } from 'src/app/interfaces/self-serve/responses/isimulation';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AssignmentModalComponent } from '../../components/modals/assignment-modal/assignment-modal.component';
import { AssignmentsService } from 'src/app/services/self-serve/assignments.service';
import { IAssignment } from 'src/app/interfaces/self-serve/assignment-manager/requests/iassignment';
import { IAssignmentCreate } from 'src/app/interfaces/self-serve/assignment-manager/responses/iassignment-create';
import { IUserAssignments } from 'src/app/interfaces/self-serve/assignment-manager/iuser-assignments';
import {
  toDateInputString,
  toIsoDateString,
} from 'src/app/utils/date-parser.util';
import { AssignmentReactiveFormService } from '../../services/assignment-reactive-form.service';
import { ButtonFloatingSelectComponent } from '../../components/button-floating-select/button-floating-select.component';
import { AddSimulationModalComponent } from '../../components/modals/add-simulation-to-assignment/add-simulation-to-assignment.component';
import {
  EntityActionType,
  EntityType,
} from 'src/app/utils/enums/self-serve/assignment-type.enum';
import { AddUserToAssignmentModalComponent } from '../../components/modals/add-users-to-assignment/add-users-to-assignment-modal.component';
import { IAccountInfo } from 'src/app/interfaces/iaccount-info';
import { AssignmentConstants } from 'src/app/utils/constants/self-serve/assignment.constants';
import { CallTypeLabelMap } from 'src/app/utils/constants/self-serve/simulation.constants';
import { IInputUser, IUsersWithTeams } from 'src/app/interfaces/self-serve/assignment-manager/responses/iusers-with-teams';
import { TeamUserParserService } from '../../services/team-user-parser.service';
import { AppConstants } from 'src/app/app-constants';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/assignments.service';
import { SIMULATION_NAME_ALREADY_EXISTS_STATUS } from 'src/app/services/self-serve/author.service';
import { IConfirmActionPayload } from 'src/app/interfaces/self-serve/requests/iconfirm-action-payload';
import { ISelfAuthoringPrefillData } from 'src/app/interfaces/self-serve/iself-authoring-prefill-data';
import { isContentLimitExceededError } from 'src/app/utils/document-upload-error.util';
import { UsersListComponent } from './users/users-list.component';
import { SelfAuthoringSimulationGenerationModalComponent } from '../../components/modals/self-authoring-simulation-generation-modal/self-authoring-simulation-generation-modal.component';
import { SimulationTypeSelectionModalComponent } from '../../components/modals/simulation-type-selection-modal/simulation-type-selection-modal.component';
import { SimulationGenerationPollingService } from 'src/app/services/self-serve/simulation-generation-polling.service';
import { SimulationGenerationStatus } from 'src/app/utils/enums/self-serve/simulation-generation-status.enum';
import { SimulationTypeEnum } from 'src/app/utils/enums/self-serve/simulation-type.enum';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'assignments-dashboard',
  standalone: true,
  templateUrl: './assignments-dashboard.component.html',
  styleUrls: ['./assignments-dashboard.component.scss'],
  imports: [
    CommonModule,
    QaiInnerWrapComponent,
    QaiIconComponent,
    QaiButtonComponent,
    QaiFloatingSelectComponent,
    QaiTabsComponent,
    QaiTabDirective,
    AssignmentsComponent,
    AssignmentSimulationsComponent,
    ActionModalComponent,
    DeleteModalComponent,
    AssignmentModalComponent,
    ButtonFloatingSelectComponent,
    AddSimulationModalComponent,
    AddUserToAssignmentModalComponent,
    UsersListComponent,
    SelfAuthoringSimulationGenerationModalComponent,
    SimulationTypeSelectionModalComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class AssignmentsDashboardComponent implements OnInit, OnChanges {
  @ViewChild(AssignmentsComponent)
  assignmentsComponent!: AssignmentsComponent;
  
  @ViewChild(QaiTabsComponent)
  tabsComponent!: QaiTabsComponent;

  @ViewChild(ButtonFloatingSelectComponent)
  createOptionsButton?: ButtonFloatingSelectComponent;

  @ViewChild('createSimulationButton', { read: ElementRef })
  createSimulationButtonRef?: ElementRef<HTMLButtonElement>;

  EntityType = EntityType;
  EntityActionType = EntityActionType;
  SimulationTypeEnum = SimulationTypeEnum;

  simulationToDelete: { id: string | number; rowData: TableRow } | null = null;
  simulationToDuplicate: { id: string; name: string; moduleUuid?: string } | null = null;
  isLoading = false;
  selectedAccount: { label: string; value: string } | undefined;
  networkRequestInProgress: boolean = false;
  assignmentRequestInProgress: boolean = false;
  selectedAccountUuid: string = '';
  isDeleteModalOpen: boolean = false;
  simulationsRefreshKey = 0;
  isAssignmentModalVisible: boolean = false;
  isUserAssignmentModalVisible: boolean = false;
  addSimulationsToAssignmentModalOpen: boolean = false;
  moduleAssignmentUuid: string = '';
  moduleUuid: string = '';
  modalErrorStatus?: number;
  isFilesExceedingTokens = false;
  createModuleOptions: { label: string; value: string }[] = [];
  deleteContext: {
    type: EntityType.SIMULATION | EntityType.ASSIGNMENT;
    id: string | number;
    rowData: TableRow;
    moduleUuid?: string;
  } | null = null;
  duplicateAssignmentModalVisible = false;
  assignmentToDuplicateName = '';
  assignmentToDuplicateId = '';
  hasSingleEligibleAccount = false;
  // Stable reference so [currentAssignees] doesn't get a new [] every change-detection cycle.
  newAssignmentCurrentAssignees: IUserAssignments[] = [];

  modalOpen = false;
  modalEntityType: EntityType = EntityType.SIMULATION;
  modalActionType: EntityActionType = EntityActionType.CREATE;
  modalOriginalName = '';
  isSelfAuthoringRoleplayEnabled: boolean = false;
  isSelfAuthoringKnowledgeCheckEnabled: boolean = false;

  simulationTypeSelectionModalOpen = false;

  selfAuthoringCreateModalOpen = false;
  selfAuthoringSimulationType: SimulationTypeEnum = SimulationTypeEnum.ROLE_PLAY;
  selfAuthoringCreateCallUuid: string | undefined = undefined;
  selfAuthoringPrefillData: ISelfAuthoringPrefillData | undefined = undefined;
  retryInProgress = false;

  @Input() assignmentsTabAccounts: IAccountInfo[] = [];
  @Input() simulationsTabAccounts: IAccountInfo[] = [];

  currentTab: 'assignments' | 'simulations' | 'users' =
    AssignmentConstants.ASSIGNMENTS_TAB;

  shouldShowAssignmentsTab: boolean = false;
  shouldShowSimulationsTab: boolean = false;
  shouldShowUsersTab: boolean = false;

  fetchedUsers: IInputUser[] = [];
  isUsersLoading: boolean = false;

  selectedAccountHasAuthor: boolean = false;
  selectedAccountHasAssignmentManager: boolean = false;

  assignmentsTabOptions: { label: string; value: string }[] = [];
  simulationsTabOptions: { label: string; value: string }[] = [];

  @ViewChild(AssignmentModalComponent)
  assignmentModalComponent!: AssignmentModalComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authorService: AuthorService,
    private assignmentService: AssignmentsService,
    private cdRef: ChangeDetectorRef,
    private authorReactiveFormService: AuthorReactiveFormService,
    private assignmentReactiveFormService: AssignmentReactiveFormService,
    public teamUserParserService: TeamUserParserService,
    private featureFlagsService: FeatureFlagsService,
    private pollingService: SimulationGenerationPollingService,
    private destroyRef: DestroyRef,
    private translocoService: TranslocoService
  ) {}

  async ngOnInit() {
    this.translocoService.selectTranslation('simulationBuilder').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.createModuleOptions = [
        { label: this.translocoService.translate('simulationBuilder.ENTITY_TYPE_ASSIGNMENT_DISPLAY'), value: 'assignment' },
        { label: this.translocoService.translate('simulationBuilder.ENTITY_TYPE_SIMULATION_DISPLAY'), value: 'simulation' },
      ];
    });

    this.hasSingleEligibleAccount = SelfServeLocalStorageUtil.hasSingleEligibleAccount();
    this.determineUserRoles();
    this.setInitialTab();
    this.prepareAccountOptions();
    this.setSelectedAccount();
    this.isSelfAuthoringRoleplayEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.SELF_AUTHORING_ROLEPLAY
    );
    this.isSelfAuthoringKnowledgeCheckEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.SELF_AUTHORING_KNOWLEDGE_CHECK
    );

    if (this.route?.snapshot?.queryParamMap) {
      this.checkQueryParameters();
    }

    this.route?.queryParamMap?.
      pipe(takeUntilDestroyed(this.destroyRef)).
      subscribe(() => {
      this.checkRetrySimulationQueryParameters();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['assignmentsTabAccounts'] ||
      changes['simulationsTabAccounts']
    ) {
      this.determineUserRoles();
      this.prepareAccountOptions();
      this.setSelectedAccount();
      
      if (this.route?.snapshot?.queryParamMap) {
        this.checkQueryParameters();
      }
    }
  }

  get options(): { label: string; value: string }[] {
    if (this.currentTab === AssignmentConstants.ASSIGNMENTS_TAB || this.currentTab === AssignmentConstants.USERS_TAB) {
      return this.assignmentsTabOptions;
    }
    return this.simulationsTabOptions;
  }

  private determineUserRoles(): void {
    this.shouldShowAssignmentsTab = this.assignmentsTabAccounts.length > 0;
    this.shouldShowSimulationsTab = this.simulationsTabAccounts.length > 0;
    this.shouldShowUsersTab = false;
    this.applyUserManagementFlag();
  }

  private async applyUserManagementFlag(): Promise<void> {
    if (this.assignmentsTabAccounts.length === 0) return;
    const flagEnabled = await this.featureFlagsService.isFlagEnabled(
      AppConstants.SELF_SERVE_USER_MANAGEMENT
    );
    this.shouldShowUsersTab = flagEnabled;
    this.cdRef.detectChanges();
  }

  private determineSelectedAccountRoles(): void {
    if (!this.selectedAccountUuid) {
      this.selectedAccountHasAuthor = false;
      this.selectedAccountHasAssignmentManager = false;
      return;
    }

    const accountRoles = SelfServeLocalStorageUtil.getSelectedAccountRoles(
      this.selectedAccountUuid
    );
    this.selectedAccountHasAuthor = accountRoles.hasAuthor;
    this.selectedAccountHasAssignmentManager =
      accountRoles.hasAssignmentManager;
  }

  private setInitialTab(): void {
    const fromSimulationDetails = this.route?.snapshot?.queryParamMap?.get('fromSimulationDetails');
    const fromUserDetails = this.route?.snapshot?.queryParamMap?.get('fromUserDetails');

    if (fromSimulationDetails === 'true') {
      this.currentTab = AssignmentConstants.SIMULATIONS_TAB;

      setTimeout(() => {
        this.selectSimulationsTab();
      }, 100);
    } else if (fromUserDetails === 'true') {
      this.currentTab = AssignmentConstants.USERS_TAB;

      setTimeout(() => {
        this.selectUsersTab();
      }, 100);
    } else if (this.shouldShowAssignmentsTab) {
      this.currentTab = AssignmentConstants.ASSIGNMENTS_TAB;
    } else if (this.shouldShowSimulationsTab) {
      this.currentTab = AssignmentConstants.SIMULATIONS_TAB;
    }
  }

  private prepareAccountOptions(): void {
    this.assignmentsTabOptions = this.mapAccountsToOptions(
      this.assignmentsTabAccounts
    );

    this.simulationsTabOptions = this.mapAccountsToOptions(
      this.simulationsTabAccounts
    );
  }

  private mapAccountsToOptions(
    accounts: IAccountInfo[]
  ): { label: string; value: string }[] {
    return accounts.map((account: IAccountInfo) => ({
      label: account.name,
      value: account.uuid,
    }));
  }

  private setSelectedAccount(): void {
    this.selectedAccountUuid =
      SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const currentAccountUuid =
      SelfServeLocalStorageUtil.getCurrentAccountUuid();

    const uuidToMatch = this.selectedAccountUuid || currentAccountUuid;
    const currentOptions = this.options;

    const matchedOption = currentOptions.find(
      (opt) => opt.value === uuidToMatch
    );

    if (matchedOption) {
      this.selectedAccount = matchedOption;
      this.valueChange(matchedOption);
      this.getAllUsersWithTeams();
    } else if (currentOptions.length > 0) {
      this.selectedAccount = currentOptions[0];
      this.valueChange(currentOptions[0]);
      this.getAllUsersWithTeams();
    } else {
      this.resetAccountSelection();
    }

    this.determineSelectedAccountRoles();
  }

  private resetAccountSelection(): void {
    this.assignmentsTabOptions = [];
    this.simulationsTabOptions = [];
    this.selectedAccount = undefined;
    SelfServeLocalStorageUtil.setSelectedSelfServeAccountUuid('');
    this.selectedAccountUuid = '';
  }

  valueChange(selectedValue: string | { label: string; value: string }): void {
    let matchedOption: { label: string; value: string } | undefined;
    const currentOptions = this.options;

    if (typeof selectedValue === 'string') {
      matchedOption = currentOptions.find((opt) => opt.value === selectedValue);
    } else {
      matchedOption = selectedValue;
    }

    if (matchedOption) {
      this.selectedAccount = matchedOption;
      SelfServeLocalStorageUtil.setSelectedSelfServeAccountUuid(
        matchedOption.value
      );
      this.selectedAccountUuid = matchedOption.value;
      this.determineSelectedAccountRoles();
    }
  }

  selectionChange(event: QaiFloatingSelectOption | undefined) {
    this.getAllUsersWithTeams();
  }

  openModal(
    entityType: EntityType,
    actionType: EntityActionType,
    originalName: string = ''
  ) {
    if (
      entityType === EntityType.ASSIGNMENT &&
      actionType === EntityActionType.CREATE
    ) {
      this.openAssignmentModal();
      return;
    }

    if (
      entityType === EntityType.SIMULATION &&
      actionType === EntityActionType.CREATE &&
      this.isSelfAuthoringCreateEnabled()
    ) {
      if (this.isSelfAuthoringKnowledgeCheckEnabled) {
        this.simulationTypeSelectionModalOpen = true;
      } else {
        this.selfAuthoringCreateModalOpen = true;
      }
      return;
    }

    this.modalOpen = true;
    this.modalEntityType = entityType;
    this.modalActionType = actionType;
    this.modalOriginalName = originalName;
  }

  private isSelfAuthoringCreateEnabled(): boolean {
    return this.isSelfAuthoringRoleplayEnabled && this.selectedAccountHasAuthor;
  }

  handleSimulationTypeSelected(type: SimulationTypeEnum): void {
    this.simulationTypeSelectionModalOpen = false;
    this.selfAuthoringSimulationType = type;
    this.selfAuthoringCreateModalOpen = true;
  }

  handleSimulationTypeSelectionDismissed(): void {
    this.simulationTypeSelectionModalOpen = false;
    this.focusCreateButton();
  }

  private focusCreateButton(): void {
    this.createOptionsButton?.focus();
    this.createSimulationButtonRef?.nativeElement.focus();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.modalOriginalName = '';
  }

  closeSelfAuthoringCreateModal(): void {
    this.selfAuthoringCreateModalOpen = false;
    this.selfAuthoringSimulationType = SimulationTypeEnum.ROLE_PLAY;
    this.modalErrorStatus = undefined;
    this.isFilesExceedingTokens = false;
    this.selfAuthoringPrefillData = undefined;
  }

  handleSelfAuthoringGenerationClosed(): void {
    this.selfAuthoringCreateModalOpen = false;
    this.selfAuthoringSimulationType = SimulationTypeEnum.ROLE_PLAY;
    this.selfAuthoringCreateCallUuid = undefined;
    this.selfAuthoringPrefillData = undefined;
    this.simulationsRefreshKey++;
  }

  handleBackToFormWithPrefill(): void {
    const callUuid = this.selfAuthoringCreateCallUuid;
    if (!callUuid) {
      return;
    }
    this.openSelfAuthoringFormWithPrefill(callUuid);
  }

  private openSelfAuthoringFormWithPrefill(callUuid: string): void {
    const generation = this.pollingService.getGeneration(callUuid);
    this.selfAuthoringPrefillData = generation && {
      callUuid: generation.callUuid,
      name: generation.simulationName,
      moduleUuid: generation.moduleUuid,
      moduleName: generation.moduleName,
      scenario: generation.scenario,
      files: generation.files ?? [],
      tagUuids: generation.tagUuids,
    };
    this.isFilesExceedingTokens = generation?.status === SimulationGenerationStatus.TokenLimitExceeded;
    this.selfAuthoringCreateCallUuid = undefined;
  }

  handleRetryClicked(): void {
    const callUuid = this.selfAuthoringCreateCallUuid;
    const generation = callUuid ? this.pollingService.getGeneration(callUuid) : undefined;
    if (!generation) {
      return;
    }

    this.retryInProgress = true;
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const customerContentUuids = (generation.files ?? []).map((file) => file.customerContentUuid);

    this.authorService
      .retryFileValidation(generation.callUuid, accountUuid, customerContentUuids)
      .subscribe({
        next: (res) => {
          this.retryInProgress = false;
          this.pollingService.trackGeneration(
            res.callUuid,
            generation.simulationName,
            generation.moduleUuid,
            generation.simulationStatus,
            {
              scenario: generation.scenario,
              moduleName: generation.moduleName,
              files: generation.files,
              tagUuids: generation.tagUuids,
            },
          );
        },
        error: (err: any) => {
          this.retryInProgress = false;
          console.error(err);
        },
      });
  }

  handleOpenGenerationModal({ callUuid, moduleUuid, name, status }: {
    callUuid: string;
    moduleUuid?: string;
    name: string;
    status: string;
  }): void {
    this.selfAuthoringCreateCallUuid = callUuid;
    this.selfAuthoringCreateModalOpen = true;
    this.moduleUuid = moduleUuid ?? '';

    this.pollingService.checkStatus(callUuid, { name, moduleUuid, status }).subscribe();
  }

  handleSelfAuthoringConfirmed(payload: IConfirmActionPayload): void {
    this.networkRequestInProgress = true;
    this.modalErrorStatus = undefined;
    this.isFilesExceedingTokens = false;
    this.moduleUuid = payload.moduleUuid ?? '';

    const amendCallUuid = this.selfAuthoringPrefillData?.callUuid;
    if (amendCallUuid) {
      this.resubmitAfterAmend(amendCallUuid, payload);
      return;
    }

    this.authorService
      .createSimulationAPI(payload.name, payload.moduleUuid, {
        initialCustomerMessage: payload.scenario,
        customerContentUuids: payload.customerContentUuids,
        tagUuids: payload.tagUuids,
        hintsVisible: payload.hintsVisible,
        referenceVisible: payload.referenceVisible,
        callType:
          this.selfAuthoringSimulationType === SimulationTypeEnum.KNOWLEDGE_CHECK
            ? AppConstants.CALL_TYPE_GUIDED
            : undefined,
      })
      .subscribe({
        next: (res: ISimulation) => {
          this.networkRequestInProgress = false;
          // Knowledge Check has no generation step, so go straight to the new simulation.
          if (this.selfAuthoringSimulationType === SimulationTypeEnum.KNOWLEDGE_CHECK) {
            this.navigateToCreatedKnowledgeCheck(res.uuid, payload);
            return;
          }
          this.pollingService.trackGeneration(
            res.uuid,
            payload.name,
            payload.moduleUuid,
            res.status?.toLocaleLowerCase(),
            {
              scenario: payload.scenario,
              moduleName: payload.moduleName,
              files: payload.documents,
              tagUuids: payload.tagUuids,
            },
          );
          this.selfAuthoringCreateCallUuid = res.uuid;
          this.selfAuthoringPrefillData = undefined;
        },
        error: (err: any) => this.handleCreateSimulationError(err),
      });
  }

  private navigateToCreatedKnowledgeCheck(simulationUuid: string, payload: IConfirmActionPayload): void {
    const generalData: any = {
      simulationName: payload.name,
    };
    if (payload.moduleUuid) {
      generalData.moduleUuid = payload.moduleUuid;
    }
    this.authorReactiveFormService.createSimulation({ general: generalData }, simulationUuid);
    this.closeSelfAuthoringCreateModal();
    const queryParams: any = {};
    if (payload.moduleUuid) {
      queryParams.moduleUuid = payload.moduleUuid;
    }
    this.router.navigate(['simulations', simulationUuid], {
      relativeTo: this.route,
      queryParams,
      state: { callType: CallTypeLabelMap.guided },
    });
  }

  private resubmitAfterAmend(callUuid: string, payload: IConfirmActionPayload): void {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    const simulationStatus = this.pollingService.getGeneration(callUuid)?.simulationStatus;
    // Tags can't be changed while amending, so keep the ones stored at creation.
    const tagUuids = this.pollingService.getGeneration(callUuid)?.tagUuids;

    this.authorService
      .retryFileValidation(callUuid, accountUuid, payload.customerContentUuids ?? [])
      .subscribe({
        next: (res) => {
          this.networkRequestInProgress = false;
          this.pollingService.trackGeneration(
            res.callUuid,
            payload.name,
            payload.moduleUuid,
            simulationStatus,
            { scenario: payload.scenario, moduleName: payload.moduleName, files: payload.documents, tagUuids },
          );
          this.selfAuthoringCreateCallUuid = res.callUuid;
          this.selfAuthoringPrefillData = undefined;
        },
        error: (err: any) => {
          this.networkRequestInProgress = false;
          console.error(err);
        },
      });
  }

  private handleCreateSimulationError(err: any): void {
    console.error(err);
    this.networkRequestInProgress = false;

    if (err?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
      this.modalErrorStatus = err.status;
    }

    const backendMessage = typeof err?.error === 'string' ? err.error : err?.error?.message;
    if (isContentLimitExceededError(backendMessage)) {
      this.isFilesExceedingTokens = true;
    }
  }

  get shouldShowTabs(): boolean {
    return (this.shouldShowAssignmentsTab && this.shouldShowSimulationsTab)
        || (this.shouldShowAssignmentsTab && this.shouldShowUsersTab)
        || (this.shouldShowSimulationsTab && this.shouldShowUsersTab);
  }

  get assignmentStatusLabel(): string | undefined {
    const statusCell = this.deleteContext?.rowData?.[
      'Assignment status'
    ] as any;
    return statusCell?.label;
  }

  onModalConfirmed(data: IConfirmActionPayload | string) {
    // Handle backward compatibility: if string is passed, treat as name only
    const name = typeof data === 'string' ? data : data.name;
    const moduleUuid = typeof data === 'string' ? undefined : data.moduleUuid;
    const scenario = typeof data === 'string' ? undefined : data.scenario;
    const customerContentUuids = typeof data === 'string' ? undefined : data.customerContentUuids;

    this.networkRequestInProgress = true;
    this.modalErrorStatus = undefined;
    this.isFilesExceedingTokens = false;

    if (this.modalEntityType === EntityType.SIMULATION) {
      if (
        this.modalActionType === EntityActionType.DUPLICATE &&
        this.simulationToDuplicate
      ) {
        this.duplicateSimulation(name, moduleUuid);
      } else {
        this.createNewSimulation(name, moduleUuid, scenario, customerContentUuids);
      }
    } else if (this.modalEntityType === EntityType.ASSIGNMENT) {
      this.duplicateAssignment(name);
    }
  }

  onArchiveSimulation(event: { id: string; moduleUuid?: string }) {
    this.authorService.archiveSimulationAPI(event.id).subscribe({
      next: () => {
        this.simulationsRefreshKey++;
      },
      error: (err: any) => {
        console.error('Error archiving simulation:', err);
      },
    });
  }

  onDuplicateSimulation(event: { id: string; name: string; moduleUuid?: string }) {
    this.simulationToDuplicate = event;
    this.openModal(
      EntityType.SIMULATION,
      EntityActionType.DUPLICATE,
      event.name
    );
  }

  onDuplicateAssignment(event: { id: string; name: string }) {
    this.assignmentToDuplicateId = event.id;
    this.openModal(
      EntityType.ASSIGNMENT,
      EntityActionType.DUPLICATE,
      event.name
    );
  }

  handleCreateOptions(value: string) {
    const entityType =
      value === 'assignment' ? EntityType.ASSIGNMENT : EntityType.SIMULATION;
    this.openModal(entityType, EntityActionType.CREATE);
  }

  private createNewSimulation(
    name: string,
    moduleUuid?: string,
    scenario?: string,
    customerContentUuids?: string[]
  ) {
    this.sendRequestToCreateSimulation(name, moduleUuid, {
      initialCustomerMessage: scenario,
      customerContentUuids,
    });
  }

  duplicateSimulation(newName: string, moduleUuid?: string) {
    if (!this.simulationToDuplicate) return;

    const finalModuleUuid = moduleUuid || this.simulationToDuplicate.moduleUuid || '';
    const payload = {
      callUuid: this.simulationToDuplicate.id,
      callTitle: newName,
      moduleUuid: finalModuleUuid,
    };

    this.authorService.duplicateSimulationAPI(payload).subscribe({
      next: (newSimulation: ISimulation) => {
        this.resetModalState();
        this.router.navigate(['simulations', newSimulation.uuid], {
          relativeTo: this.route,
          queryParams: { moduleUuid: finalModuleUuid },
        });
      },
      error: (err: any) => {
        console.error('Error duplicating simulation:', err);
        this.networkRequestInProgress = false;
        
        if (err?.status === SIMULATION_NAME_ALREADY_EXISTS_STATUS) {
          this.modalErrorStatus = err.status;
        }
      },
      complete: () => {
        this.networkRequestInProgress = false;
      },
    });
  }

  duplicateAssignment(newName: string) {
    if (!this.assignmentToDuplicateId) return;

    const payload = {
      assignmentUuid: this.assignmentToDuplicateId,
      assignmentName: newName,
    };

    this.assignmentService.duplicateAssignmentAPI(payload).subscribe({
      next: (newAssignment: any) => {
        this.resetModalState();
        this.router.navigate(['assignments', newAssignment.uuid], {
          relativeTo: this.route,
        });
      },
      error: (err: any) => {
        console.error('Error duplicating assignment:', err);
        this.networkRequestInProgress = false;
        
        if (err?.status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS) {
          this.modalErrorStatus = err.status;
        }
      },
      complete: () => {
        this.networkRequestInProgress = false;
      },
    });
  }

  afterClose() {
    console.log('Simulation dialog is closed');
  }

  public resetModalState() {
    this.modalOpen = false;
    this.modalOriginalName = '';
    this.simulationToDuplicate = null;
    this.assignmentToDuplicateId = '';
    this.assignmentToDuplicateName = '';
    this.modalErrorStatus = undefined;
    this.isFilesExceedingTokens = false;
    this.cdRef.detectChanges();
  }

  sendRequestToCreateSimulation(
    name: string,
    moduleUuid?: string,
    options?: { initialCustomerMessage?: string; customerContentUuids?: string[] }
  ): void {
    this.authorService.createSimulationAPI(name, moduleUuid, options).subscribe({
      next: (res: ISimulation) => {
        console.log(res);
        const generalData: any = {
          simulationName: name,
        };
        if (moduleUuid) {
          generalData.moduleUuid = moduleUuid;
        }
        this.authorReactiveFormService.createSimulation(
          {
            general: generalData,
          },
          res.uuid
        );
        this.resetModalState();
        const queryParams: any = {};
        if (moduleUuid) {
          queryParams.moduleUuid = moduleUuid;
        }
        this.router.navigate(['simulations', res.uuid], {
          relativeTo: this.route,
          queryParams,
        });
      },
      error: (err) => this.handleCreateSimulationError(err),
      complete: () => {
        this.networkRequestInProgress = false;
      },
    });
  }

  onDeleteItem(
    type: EntityType.SIMULATION | EntityType.ASSIGNMENT,
    event: { id: string | number; rowData: TableRow; moduleUuid?: string }
  ) {
    this.deleteContext = {
      type,
      id: event.id,
      rowData: event.rowData,
      moduleUuid: event.moduleUuid,
    };
    this.isDeleteModalOpen = true;
    this.cdRef.detectChanges();
  }

  handleDeleteConfirmed() {
    if (!this.deleteContext) return;

    this.networkRequestInProgress = true;
    const { type, id, moduleUuid } = this.deleteContext;

    const delete$ =
      type === EntityType.SIMULATION
        ? this.authorService.deleteSimulationAPI(id as string, moduleUuid || '')
        : this.assignmentService.deleteStagedAssignmentAPI(id as string);

    delete$.subscribe({
      next: () => {
        this.closeDeleteModal();
        if (type === EntityType.SIMULATION) {
          this.simulationsRefreshKey++;
        } else if (type === EntityType.ASSIGNMENT) {
          this.assignmentsComponent.loadAssignments();
        }
      },
      error: (err: any) => {
        console.error(`Error deleting ${type}:`, err);
        this.closeDeleteModal();
      },
    });
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen = false;
    this.deleteContext = null;
    this.resetNetworkRequestState();
  }

  onDeleteSimulation(event: { id: string | number; rowData: TableRow }) {
    this.simulationToDelete = event;
    this.isDeleteModalOpen = true;
  }

  openAssignmentModal(uuid?: string) {
    this.assignmentModalComponent.moduleAssignmentUuid = uuid ?? null;
    this.isAssignmentModalVisible = true;
  }

  openAddSimulationToAssignmentModal() {
    if (this.assignmentModalComponent?.form) {
      const uuid = this.assignmentModalComponent.getModuleUuid();
      if (uuid) {
        this.moduleUuid = uuid;
      }
    }
    this.addSimulationsToAssignmentModalOpen = true;
  }

  private resetModuleAssignmentUuid(): void {
    this.moduleAssignmentUuid = '';
  }

  private resetNetworkRequestState(): void {
    this.networkRequestInProgress = false;
  }

  closeAssignmentModal(): void {
    this.isAssignmentModalVisible = false;
    this.resetNetworkRequestState();
    this.resetModuleAssignmentUuid();
  }

  closeAddSimulationToAssignmentModal(): void {
    this.addSimulationsToAssignmentModalOpen = false;
    this.resetModuleAssignmentUuid();
  }

  closeAddSimulationAndOpenAssignmentModal(): void {
    this.addSimulationsToAssignmentModalOpen = false;
    this.openAssignmentModal(this.moduleAssignmentUuid);
  }

  openUserAssignmentModal(): void {
    this.isUserAssignmentModalVisible = true;
  }

  closeUserAssignmentModal(): void {
    this.isUserAssignmentModalVisible = false;
    this.resetModuleAssignmentUuid();
  }

  handlePostUserAssignmentModal() {
    this.isUserAssignmentModalVisible = false;
    this.router.navigate(['assignments', this.moduleAssignmentUuid], {
      relativeTo: this.route,
    });
  }

  handleSimulationAssigned() {
    this.addSimulationsToAssignmentModalOpen = false;
    this.openUserAssignmentModal();
  }

  handleAssignmentSave(assignment: IAssignment, openSimulationModal: boolean) {
    console.log('Assignment saved:', assignment);
    this.assignmentRequestInProgress = true;

    const payload = {
      ...assignment,
      startDate: toIsoDateString(assignment.startDate),
      endDate: toIsoDateString(assignment.endDate),
      showMissedFacts: assignment.showMissedFacts,
    };

    if (!this.moduleAssignmentUuid) {
      this.createAssignment(payload, openSimulationModal);
      return;
    }
    this.updateAssignment(
      this.moduleAssignmentUuid,
      payload,
      openSimulationModal
    );
  }

  private buildDefaultData(res: IAssignmentCreate) {
    return {
      details: {
        moduleAssignmentName: res.name,
        moduleUuid: res.moduleUuid,
        startDate: toDateInputString(res.startDate ?? ''),
        endDate: toDateInputString(res.endDate ?? ''),
        scoreCriteria: res.scoreCriteria,
        description: res.description,
        moduleAssignmentType: res.moduleAssignmentType,
        pauseEnabled: res.pauseEnabled,
        hideResultsForLearners: res.hideResultsForLearners,
        showMissedFacts: res.showMissedFacts,
      },
    };
  }

  private createAssignment(payload: IAssignment, openSimulationModal: boolean) {
    const { moduleUuid: _, ...createPayload } = payload;
    this.assignmentService.createAssignmentAPI(createPayload).subscribe({
      next: (res: IAssignmentCreate) => {
        const defaultData = this.buildDefaultData(res);
        this.assignmentReactiveFormService.createAssignment(
          defaultData,
          res.uuid
        );
        this.moduleAssignmentUuid = res.uuid;
        this.moduleUuid = res.moduleUuid || '';
        this.handlePostAssignmentSave(res, openSimulationModal);
        this.assignmentsComponent.loadAssignments();
      },  
      error: (err) => {
        console.error(err);
        if (err?.status === ASSIGNMENT_NAME_ALREADY_EXISTS_STATUS) {
          this.assignmentReactiveFormService.setAssignmentNameConflict(true);
        }
        this.assignmentRequestInProgress = false;
      },
      complete: () => {
        this.assignmentRequestInProgress = false;
      },
    });
  }

  private updateAssignment(
    uuid: string,
    payload: IAssignment,
    openSimulationModal: boolean
  ) {
    this.assignmentService.updateAssignment(uuid, payload).subscribe({
      next: (res: IAssignmentCreate) => {
        this.moduleUuid = res.moduleUuid || payload.moduleUuid || '';
        this.handlePostAssignmentSave(res, openSimulationModal);
        this.assignmentsComponent.loadAssignments();
      },
      error: (err) => {
        console.error(err);
        this.assignmentRequestInProgress = false;
      },
      complete: () => {
        this.assignmentRequestInProgress = false;
      },
    });
  }

  private handlePostAssignmentSave(
    res: IAssignmentCreate,
    openSimulationModal: boolean
  ) {
    this.isAssignmentModalVisible = false;
    if (openSimulationModal) {
      this.openAddSimulationToAssignmentModal();
    } else {
      this.router.navigate(['assignments', res.uuid], {
        relativeTo: this.route,
      });
    }
  }

  onTabChange(event: any) {
    const tabId = event?.id;
    if (tabId === 'uniqueId1') {
      this.currentTab = AssignmentConstants.ASSIGNMENTS_TAB;
      if (this.route?.snapshot?.queryParams) {
        this.removeSimulationDetailsQuery();
        this.removeUserDetailsQuery();
      }
    } else if (tabId === 'uniqueId2') {
      this.currentTab = AssignmentConstants.SIMULATIONS_TAB;
      if (this.route?.snapshot?.queryParams) {
        this.removeUserDetailsQuery();
      }
    } else if (tabId === 'uniqueId3') {
      this.currentTab = AssignmentConstants.USERS_TAB;
      if (this.route?.snapshot?.queryParams) {
        this.removeSimulationDetailsQuery();
      }
    }

    this.setSelectedAccount();
  }

  private removeSimulationDetailsQuery(): void {
    if (!this.route?.snapshot?.queryParams) {
      return;
    }

    const currentParams = { ...this.route.snapshot.queryParams };
    if (currentParams['fromSimulationDetails']) {
      delete currentParams['fromSimulationDetails'];
      this.router.navigate([], {
        queryParams: currentParams,
        replaceUrl: true
      });
    }
  }

  private removeUserDetailsQuery(): void {
    if (!this.route?.snapshot?.queryParams) {
      return;
    }

    const currentParams = { ...this.route.snapshot.queryParams };
    if (currentParams['fromUserDetails']) {
      delete currentParams['fromUserDetails'];
      this.router.navigate([], {
        queryParams: currentParams,
        replaceUrl: true
      });
    }
  }

  private checkQueryParameters(): void {
    const fromSimulationDetails = this.route?.snapshot?.queryParamMap?.get('fromSimulationDetails');
    const fromUserDetails = this.route?.snapshot?.queryParamMap?.get('fromUserDetails');

    if (fromSimulationDetails === 'true' && this.shouldShowSimulationsTab) {
      this.currentTab = AssignmentConstants.SIMULATIONS_TAB;

      setTimeout(() => {
        this.selectSimulationsTab();
      }, 100);
      this.cdRef.detectChanges();
    } else if (fromUserDetails === 'true' && this.shouldShowUsersTab) {
      this.currentTab = AssignmentConstants.USERS_TAB;

      setTimeout(() => {
        this.selectUsersTab();
      }, 100);
      this.cdRef.detectChanges();
    }
  }

  /** Reopens the create-simulation modal, prefilled, when arriving via the "View documents" banner CTA. */
  private checkRetrySimulationQueryParameters(): void {
    const callUuid = this.route?.snapshot?.queryParamMap?.get('retrySimulationCallUuid');
    if (!callUuid) {
      return;
    }

    this.openSelfAuthoringFormWithPrefill(callUuid);
    this.selfAuthoringCreateModalOpen = true;
    this.removeRetrySimulationQuery();
  }

  private removeRetrySimulationQuery(): void {
    if (!this.route?.snapshot?.queryParams) {
      return;
    }

    const currentParams = { ...this.route.snapshot.queryParams };
    if (currentParams['retrySimulationCallUuid']) {
      delete currentParams['retrySimulationCallUuid'];
      this.router.navigate([], {
        queryParams: currentParams,
        replaceUrl: true
      });
    }
  }

  private selectSimulationsTab(): void {
      this.tabsComponent?.selectTabById('uniqueId2');
  }

  private selectUsersTab(): void {
      this.tabsComponent?.selectTabById('uniqueId3');
  }

  isComingFromSimulationDetails(): boolean {
    const fromSimulationDetails = this.route?.snapshot?.queryParamMap?.get('fromSimulationDetails');
    return fromSimulationDetails === 'true';
  }

  handleChangeAssignmentStatus(event: AssignmentStatusChange): void {
    this.assignmentService.updateAssignmentStatus(event.uuid, event.targetStatus).subscribe({
      next: (res) => {
        console.log(`Assignment final status: ${res.status}`);
        this.assignmentsComponent.loadAssignments();
      },
      error: (err: any) => {
        console.error('Error updating assignment status:', err);
      },
    });
  }

  getAllUsersWithTeams(): void {
    this.isUsersLoading = true;
    this.assignmentService.getAllUsersWithTeams().subscribe({
      next: (usersWithTeams: IUsersWithTeams) => {
        this.fetchedUsers = usersWithTeams.users ?? [];
        const allTeamsWithUsers =
          this.teamUserParserService.transformTeamUserData(usersWithTeams);
        SelfServeLocalStorageUtil.setTransformedTeamsWithUsers(
          JSON.stringify(allTeamsWithUsers)
        );
        this.isUsersLoading = false;
      },
      error: (err: any) => {
        console.error('Failed to users with teams', err);
        this.isUsersLoading = false;
      },
    });
  }
}
